/* Responsibility: Filter on departureDate with optional flexible departure */
import preconditions from '../lib/preconditions.js';

const dateFormat = 'DD.MM.YYYY';
const datePattern = /^(\d{1,2}).(\d{1,2}).(\d{4})$/;

const checkPreconditions = preconditions('departureDateFilter', {
    dateInput: 'element',
    flexibleDeparture: 'select',
    createCalendar: 'function',
    resultViewCriteria: 'object',
    eventHub: 'object',
    urlKey: 'string',
    urlQueryParams: 'object',
});

export default function setupDepartureDateFilter(params) {
    checkPreconditions(params);

    let departureCalendar;
    let lastDate = {};
    const flexibleDeparture = params.flexibleDeparture;
    const resultViewCriteria = params.resultViewCriteria;
    const eventHub = params.eventHub;
    let defaultFlexibleDepartureIndex;

    function setupEventListeners() {
        const saveLastDateEntered = function () {
            lastDate = {
                date: params.dateInput.value,
                flexDays: flexibleDeparture.value,
            };
        };

        const hasChanged = function (date, flexDays) {
            return date !== lastDate.date || flexDays !== lastDate.flexDays;
        };

        const isValid = function (date, flexDays) {
            return datePattern.test(date) && flexDays >= 0;
        };

        const currentDate = function () {
            return moment(params.dateInput.value, dateFormat);
        };

        const currentFrom = function () {
            return currentDate().subtract(flexibleDeparture.value, 'days');
        };

        const currentTo = function () {
            return currentDate().add(flexibleDeparture.value, 'days');
        };

        const reset = function () {
            params.dateInput.value = '';
            flexibleDeparture.selectedIndex = defaultFlexibleDepartureIndex;
            delete resultViewCriteria[params.urlKey];
            saveLastDateEntered();
            eventHub.publish('filters:removed', params.urlKey);
        };

        const publishFiltersAdded = function () {
            eventHub.publish('filters:added', {
                filterId: params.urlKey,
                urlValue: `${currentFrom().format(dateFormat)}-${currentTo().format(dateFormat)}`,
                text: `Utreise: ${currentFrom().format('D. MMM.')} - ${currentTo().format('D. MMM.')}`,
                remove: reset,
                urlKey: params.urlKey,
            });
        };

        const changeFilter = function () {
            const date = params.dateInput.value;
            const flexDays = flexibleDeparture.value;
            if (isValid(date, flexDays) && hasChanged(date, flexDays)) {
                saveLastDateEntered();
                resultViewCriteria[params.urlKey] = `${currentFrom().format(dateFormat)}-${currentTo().format(dateFormat)}`;
                publishFiltersAdded();
                eventHub.publish('resultViewCriteria:changed');
            }
        };

        function createFlexibilityOption(days) {
            const newOption = document.createElement('option');
            newOption.setAttribute('value', days);
            newOption.innerText = `±${days} dager`;
            return newOption;
        }

        function setFlexibility(fromDate, toDate) {
            let diff = toDate.diff(fromDate, 'days');
            if (diff % 2 !== 0) {
                diff += 1;
            }
            const flexibilityOption = diff / 2;
            for (let x = 0; x < flexibleDeparture.options.length; x += 1) {
                if (+flexibleDeparture.options[x].value === flexibilityOption) {
                    flexibleDeparture.selectedIndex = x;
                    break;
                }
                if (+flexibleDeparture.options[x].value > flexibilityOption) {
                    flexibleDeparture.insertBefore(createFlexibilityOption(flexibilityOption), flexibleDeparture.options[x]);
                    flexibleDeparture.selectedIndex = x;
                    break;
                }
                if (x === flexibleDeparture.options.length - 1 && flexibilityOption > flexibleDeparture.options[x].value) {
                    flexibleDeparture.appendChild(createFlexibilityOption(flexibilityOption));
                    flexibleDeparture.selectedIndex = x + 1;
                    break;
                }
            }
            return flexibilityOption;
        }

        function setStateFromUrl() {
            if (params.urlQueryParams[params.urlKey] && params.urlQueryParams[params.urlKey].length) {
                const dates = params.urlQueryParams[params.urlKey][0].split('-');
                const fromDate = moment(dates[0], dateFormat);
                if (!fromDate.isValid()) {
                    reset();
                    return;
                }
                let toDate = fromDate.clone();
                let parsedToDate = false;
                if (dates.length > 1) {
                    const attemptedToDate = moment(dates[1], dateFormat);
                    if (attemptedToDate.isValid()) {
                        toDate = attemptedToDate;
                        parsedToDate = true;
                        if (attemptedToDate.diff(fromDate, 'days') > 364) {
                            toDate = fromDate.clone().add(364, 'days');
                        }
                    }
                }
                const dateInputExtraDays = parsedToDate ? setFlexibility(fromDate, toDate) : 0;

                params.dateInput.value = fromDate.add(dateInputExtraDays, 'days').format(dateFormat);
                resultViewCriteria[params.urlKey] = `${currentFrom().format(dateFormat)}-${currentTo().format(dateFormat)}`;
                publishFiltersAdded();
            } else {
                reset();
            }
        }

        setStateFromUrl();

        departureCalendar.subscribe('change', changeFilter);
        flexibleDeparture.onchange = changeFilter;
        eventHub.subscribe('urlQueryParams:changed', setStateFromUrl);
    }

    function createDepartureCalendar(stats) {
        const firstMoment = stats && stats.departureDate ? moment(stats.departureDate.min) : moment();
        const lastMoment = stats && stats.departureDate ? moment(stats.departureDate.max) : firstMoment.clone().add(1, 'y');
        return params.createCalendar(params.dateInput, {
            blackout(date) {
                return firstMoment.isAfter(date, 'day') || lastMoment.isBefore(date, 'day');
            },
            months: 1,
            viewStartDate: firstMoment,
            format: dateFormat,
            root: document.body,
        });
    }

    eventHub.subscribe('first-results', (response) => {
        departureCalendar = createDepartureCalendar(response ? response.stats : null);
        defaultFlexibleDepartureIndex = flexibleDeparture.selectedIndex;
        setupEventListeners();
    });
}
